import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { 
  Dropdown, 
  Button, 
  Space, 
  Typography, 
  Tag, 
  Avatar,
  Spin
} from 'antd';
import { 
  UserOutlined, 
  CrownOutlined, 
  SettingOutlined,
  LogoutOutlined,
  ProfileOutlined,
  LoginOutlined,
  UserAddOutlined,
  BankOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useUser, User } from '../../contexts/UserContext';
import { LoginDialog } from './LoginDialog';
import { UserProfileDialog } from '../Profile/UserProfileDialog';
import { CompanyProfileDialog } from '../Profile/CompanyProfileDialog';
import {
  COMPANY_CREATE_INSTRUCTION,
  COMPANY_MENU_INSTRUCTION_SHORT,
  COMPANY_SELECTOR_ERROR_RECOVERY,
  COMPANY_UNREACHABLE_INSTRUCTION,
} from '../../utils/companySelectionHelp';
import { registerHrefWithNext } from '../../utils/onboardingNavigation';
import menuStyles from './HeaderUserMenu.module.css';
import {
  personalHubControlBridgePath,
  resolvePersonalModelSlug,
} from '../../utils/resolvePersonalModelSlug';

const { Text } = Typography;

export interface HeaderUserMenuHandle {
  openAccountMenu: () => void;
  closeAccountMenu: () => void;
}

interface HeaderUserMenuProps {
  onUserChange?: (user: User | null) => void;
  /** When `appHeader`, matches design-system AppHeader user pill. */
  variant?: 'default' | 'appHeader';
}

export const HeaderUserMenu = forwardRef<HeaderUserMenuHandle, HeaderUserMenuProps>(function HeaderUserMenu(
  { onUserChange, variant = 'default' },
  ref
) {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    // isLoading,
    // error,
    selectUser,
    getAvailableUsers,
    logout,
    selectedCompany,
    availableCompanies,
    unreachableCompanies,
    selectCompany,
    loadUserCompanies
  } = useUser();

  const personalModelSlug = resolvePersonalModelSlug(user);

  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setErrorState] = useState<string | null>(null);
  const [loginDialogVisible, setLoginDialogVisible] = useState(false);
  const [userProfileDialogVisible, setUserProfileDialogVisible] = useState(false);
  const [companyProfileDialogVisible, setCompanyProfileDialogVisible] = useState(false);
  const [selectedCompanyForProfile, setSelectedCompanyForProfile] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    openAccountMenu: () => setMenuOpen(true),
    closeAccountMenu: () => setMenuOpen(false),
  }));

  // Load users on mount (only in development mode)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      void loadUsers();
    }
  }, []);

  // Notify parent of user changes
  useEffect(() => {
    onUserChange?.(user);
  }, [user, onUserChange]);

  // Debug logging for company data (only in development, and only when state actually changes)
  // Skip logging when no companies are available to reduce noise
  useEffect(() => {
    // Only log in development mode, when companies are available, and only on meaningful changes
    if (process.env.NODE_ENV === 'development' && availableCompanies.length > 0 && selectedCompany) {
      const logData = {
        companyId: selectedCompany.id,
        companyName: selectedCompany.name,
        availableCompaniesCount: availableCompanies.length
      };
      console.log('[HeaderUserMenu] Company state:', logData);
    }
    // Intentionally not logging when no company is selected or no companies available to reduce console noise
  }, [selectedCompany?.id, availableCompanies.length]); // Only log when these specific values change

  const loadUsers = async () => {
    setLoading(true);
    setErrorState(null);
    
    try {
      const users = await getAvailableUsers();
      setAvailableUsers(users);
    } catch (err: any) {
      setErrorState(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = async (userId: string) => {
    try {
      await selectUser(userId);
    } catch (err: any) {
      setErrorState(err.message || 'Failed to select user');
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleCompanyChange = async (companyId: string) => {
    try {
      await selectCompany(companyId);
      setMenuOpen(false);
    } catch (err: unknown) {
      console.error('Failed to select company:', err);
    }
  };

  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open);
    if (open && isAuthenticated && user) {
      void loadUserCompanies();
    }
  };

  // Create menu items for company selection
  const companyMenuItems = availableCompanies.map((userCompany) => {
    const isSelected = selectedCompany?.id === userCompany.company.id;
    return {
      key: userCompany.company.id,
      label: (
        <Space>
          {isSelected && <CheckOutlined style={{ color: '#52c41a' }} />}
          <BankOutlined />
          <Text strong={isSelected}>{userCompany.company.name}</Text>
          {userCompany.company.kryspace_slug ? (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {userCompany.company.kryspace_slug}
            </Text>
          ) : null}
          {userCompany.instance.instance_name && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ({userCompany.instance.instance_name})
            </Text>
          )}
        </Space>
      ),
      onClick: () => handleCompanyChange(userCompany.company.id),
      style: isSelected ? { backgroundColor: '#e6f7ff' } : {}
    };
  });

  const unreachableCompanyItems = unreachableCompanies.map((userCompany) => ({
    key: `unreachable-${userCompany.company.id}`,
    label: (
      <Space>
        <BankOutlined style={{ color: '#d97706' }} />
        <Text type="secondary">{userCompany.company.name}</Text>
        {userCompany.company.kryspace_slug ? (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {userCompany.company.kryspace_slug}
          </Text>
        ) : null}
        <Text type="warning" style={{ fontSize: '11px' }}>
          unavailable
        </Text>
      </Space>
    ),
  }));

  const companySectionFooter = (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
      {unreachableCompanies.length > 0 ? (
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
          {COMPANY_UNREACHABLE_INSTRUCTION}
        </Text>
      ) : null}
      {availableCompanies.length === 0 && unreachableCompanies.length > 0 ? (
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
          {COMPANY_SELECTOR_ERROR_RECOVERY}
        </Text>
      ) : null}
      <Space size="small" wrap>
        <Button size="small" type="primary" onClick={() => navigate('/register')}>
          Create company
        </Button>
        <Button size="small" type="link" onClick={() => void loadUserCompanies()}>
          Reload list
        </Button>
      </Space>
    </div>
  );

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <CrownOutlined style={{ color: '#fa8c16' }} />;
      case 'architect':
        return <SettingOutlined style={{ color: '#1890ff' }} />;
      case 'operator':
        return <UserOutlined style={{ color: '#52c41a' }} />;
      default:
        return <UserOutlined />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'orange';
      case 'architect':
        return 'blue';
      case 'operator':
        return 'green';
      default:
        return 'default';
    }
  };

  // Create menu items for user selection
  const userMenuItems = availableUsers.map(user => ({
    key: user.id,
    label: (
      <Space>
        {getRoleIcon(user.role)}
        <Text>{user.fullName || user.username || user.email || 'User'}</Text>
        <Tag color={getRoleColor(user.role)}>
          {user.role}
        </Tag>
      </Space>
    ),
    onClick: () => handleUserChange(user.id)
  }));

  // Create the main menu
  const menuItems = [
    // User Selection Section (only in development mode when not authenticated)
    ...(process.env.NODE_ENV === 'development' && !isAuthenticated ? [
      {
        key: 'user-selection',
        label: (
          <div>
            <Text strong>Select User:</Text>
            {loading ? (
              <div style={{ padding: '8px 0' }}>
                <Spin size="small" />
              </div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {userMenuItems.map(item => (
                  <div
                    key={item.key}
                    onClick={item.onClick}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      margin: '4px 0'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        ),
        type: 'group' as const
      },
      {
        type: 'divider' as const
      }
    ] : []),
    
    // Current User Section
    ...(isAuthenticated && user ? [
      {
        key: 'current-user',
        label: (
          <div style={{ padding: '4px 0' }}>
            <Space>
              <Avatar size="small" icon={<UserOutlined />} />
              <div>
                <div>
                  <Text strong>{user.fullName || user.username || user.email || 'User'}</Text>
                </div>
                <div>
                  <Tag color={getRoleColor(user.role)}>
                    {user.role}
                  </Tag>
                </div>
              </div>
            </Space>
          </div>
        ),
        type: 'group' as const
      },
      {
        type: 'divider' as const
      },
      // Company Selection Section
      {
        key: 'company-selection',
        label: (
          <div>
            <Text strong>Select Company:</Text>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
              {availableCompanies.length > 0 || unreachableCompanies.length > 0
                ? 'Choose a workspace below. Your selection applies across Control Bridge, artifacts, and render surfaces.'
                : COMPANY_MENU_INSTRUCTION_SHORT}
            </Text>
            {personalModelSlug ? (
              <div
                role="button"
                tabIndex={0}
                data-testid="personal-hub-entry"
                onClick={() => {
                  navigate(personalHubControlBridgePath(personalModelSlug));
                  setMenuOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(personalHubControlBridgePath(personalModelSlug));
                    setMenuOpen(false);
                  }
                }}
                style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  border: '1px solid #dbeafe',
                  background: '#f0f7ff',
                }}
              >
                <Text strong>Personal hub</Text>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  {personalModelSlug}
                </Text>
              </div>
            ) : null}
            {availableCompanies.length > 0 ? (
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '8px' }}>
                {companyMenuItems.map(item => (
                  <div
                    key={item.key}
                    onClick={item.onClick}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      margin: '4px 0',
                      ...item.style
                    }}
                    onMouseEnter={(e) => {
                      if (!item.style?.backgroundColor) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!item.style?.backgroundColor) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            ) : null}
            {unreachableCompanies.length > 0 ? (
              <div style={{ marginTop: availableCompanies.length > 0 ? 8 : 0 }}>
                <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 4 }}>
                  Unavailable on this server
                </Text>
                {unreachableCompanyItems.map((item) => (
                  <div
                    key={item.key}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '4px',
                      margin: '4px 0',
                      backgroundColor: '#fffbeb',
                      cursor: 'not-allowed',
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            ) : null}
            {availableCompanies.length > 0 || unreachableCompanies.length > 0 ? (
              companySectionFooter
            ) : (
              <div style={{ marginTop: '8px', padding: '8px 0' }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
                  {COMPANY_CREATE_INSTRUCTION}
                </Text>
                <Space size="small" wrap>
                  <Button size="small" type="primary" onClick={() => navigate('/register')}>
                    Create company
                  </Button>
                  <Button size="small" type="link" onClick={() => void loadUserCompanies()}>
                    Reload list
                  </Button>
                </Space>
              </div>
            )}
          </div>
        ),
        type: 'group' as const
      },
      {
        type: 'divider' as const
      },
      {
        key: 'profile',
        icon: <ProfileOutlined />,
        label: 'User Profile',
        onClick: () => {
          setUserProfileDialogVisible(true);
        }
      },
      // Company Profile menu items
      ...(availableCompanies.length > 0 ? [
        {
          type: 'divider' as const
        },
        ...availableCompanies.map((userCompany) => ({
          key: `company-profile-${userCompany.company.id}`,
          icon: <BankOutlined />,
          label: `Company Profile: ${userCompany.company.name}`,
          onClick: () => {
            setSelectedCompanyForProfile(userCompany.company.id);
            setCompanyProfileDialogVisible(true);
          }
        }))
      ] : []),
      {
        type: 'divider' as const
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Logout',
        onClick: handleLogout,
        danger: true
      }
    ] : [
      {
        key: 'register',
        icon: <UserAddOutlined />,
        label: 'Create account',
        onClick: () => {
          navigate(registerHrefWithNext());
        },
      },
      {
        key: 'login',
        icon: <LoginOutlined />,
        label: 'Login',
        onClick: () => {
          setLoginDialogVisible(true);
        },
      },
    ])
  ];

  const menu = {
    items: menuItems,
    style: { minWidth: '200px' }
  };

  const userInitial = (user?.fullName || user?.username || user?.email || 'U').charAt(0).toUpperCase();

  const trigger =
    variant === 'appHeader' ? (
      <button type="button" className={menuStyles.appHeaderTrigger} aria-label="Account menu">
        {selectedCompany ? (
          <span
            className={menuStyles.companyLabel}
            title={
              availableCompanies.length > 1
                ? `${selectedCompany.name} — click to switch among ${availableCompanies.length} companies`
                : selectedCompany.name
            }
          >
            {selectedCompany.name}
            {availableCompanies.length > 1 ? ` (${availableCompanies.length})` : ''}
          </span>
        ) : isAuthenticated && user ? (
          <span className={menuStyles.selectCompanyPrompt} title="Open account menu to select company">
            Select company
          </span>
        ) : null}
        <span
          className={`${menuStyles.avatar} ${!isAuthenticated || !user ? menuStyles.avatarGuest : ''}`}
        >
          {isAuthenticated && user ? userInitial : <UserOutlined style={{ fontSize: 14 }} />}
        </span>
      </button>
    ) : (
      <Button
        type="text"
        style={{
          padding: '4px 8px',
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          height: '40px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f3f4f6';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Space size="small">
          {selectedCompany && (
            <Text
              style={{
                fontSize: '12px',
                color: '#1890ff',
                fontWeight: 500,
                maxWidth: '150px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={
                availableCompanies.length > 1
                  ? `${selectedCompany.name} — click to switch among ${availableCompanies.length} companies`
                  : selectedCompany.name
              }
            >
              {selectedCompany.name}
              {availableCompanies.length > 1 ? ` (${availableCompanies.length})` : ''}
            </Text>
          )}
          <div
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: isAuthenticated && user ? '#1890ff' : '#e5e7eb',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {isAuthenticated && user ? (
              <Text style={{ fontSize: '14px', fontWeight: '500', color: 'white' }}>{userInitial}</Text>
            ) : (
              <UserOutlined style={{ fontSize: '16px', color: '#6b7280' }} />
            )}
          </div>
        </Space>
      </Button>
    );

  return (
    <>
      <Dropdown
        menu={menu}
        trigger={['click']}
        placement="bottomRight"
        open={menuOpen}
        onOpenChange={handleMenuOpenChange}
      >
        {trigger}
      </Dropdown>
      <LoginDialog 
        visible={loginDialogVisible} 
        onClose={() => setLoginDialogVisible(false)} 
      />
      <UserProfileDialog
        visible={userProfileDialogVisible}
        onClose={() => setUserProfileDialogVisible(false)}
      />
      <CompanyProfileDialog
        visible={companyProfileDialogVisible}
        companyId={selectedCompanyForProfile}
        onClose={() => {
          setCompanyProfileDialogVisible(false);
          setSelectedCompanyForProfile(null);
        }}
        onUpdated={async () => {
          // Reload companies to get fresh data
          await loadUserCompanies();
          
          // If the updated company is the currently selected one, refresh its name
          if (selectedCompanyForProfile && selectedCompany?.id === selectedCompanyForProfile) {
            // The loadUserCompanies will update selectedCompany automatically if it finds a match
            // But we need to ensure the name is updated - this happens in loadUserCompanies
            // when it finds the company in validCompanies and updates selectedCompany
          }
        }}
      />
    </>
  );
});
